import { type FacilityHit, type PersonHit } from "@/components/AlgoliaSearch";
import {
  entityHeadline,
  globalSearchFacilityListItemProps,
  globalSearchPersonListItemProps
} from "@/components/GlobalSearch/entityListItemContent";
import type { ListItemProps } from "@/components/ListItem/ListItem";
import type { TagType } from "@/components/Tag/Tag";
import type { MentionUser } from "./types";

function mentionLabel(user: MentionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
}

const EVENT_STATUS_TAGS: Record<string, { tagText: string; tagType: TagType }> = {
  Active: { tagText: "Active", tagType: "success" },
  Pending: { tagText: "Pending", tagType: "warn" },
  Draft: { tagText: "Draft", tagType: "information" },
  PUBLISHED: { tagText: "Published", tagType: "success" },
  Canceled: { tagText: "Canceled", tagType: "surface" },
  Cancelled: { tagText: "Canceled", tagType: "surface" },
  Completed: { tagText: "Completed", tagType: "surface" }
};

function eventStatusTag(
  status?: string | null
): Pick<ListItemProps, "tagText" | "tagType" | "tagStyle" | "tagPosition"> {
  if (!status) return {};
  const mapped =
    EVENT_STATUS_TAGS[status] ?? EVENT_STATUS_TAGS[status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()];
  if (!mapped) return {};
  return { tagText: mapped.tagText, tagType: mapped.tagType, tagStyle: "strong", tagPosition: "trailing" };
}

function displayedFacilityName(user: MentionUser): string | undefined {
  const name = user.facilityName?.trim();
  if (!name || name === "Unemployed") return undefined;
  return name;
}

function mentionPersonHit(user: MentionUser): PersonHit {
  const name = mentionLabel(user);
  const pgaId = user.pgaId?.trim() || "";
  return {
    objectID: user.id,
    object_type: "Person",
    pga_id: pgaId,
    name,
    preferred_name: name,
    profile_photo: user.photo,
    facility_name: displayedFacilityName(user),
    other_facility_count: user.otherFacilityCount,
    section_name: user.sectionName ?? undefined,
    state: user.state ?? undefined,
    legacy_id: pgaId
  };
}

function mentionFacilityHit(user: MentionUser): FacilityHit {
  const name = mentionLabel(user);
  const city = user.city?.trim() || "";
  const state = user.state?.trim() || "";
  return {
    objectID: user.id,
    object_type: "Facility",
    facility_id: user.expandContext?.type === "Facility" ? user.expandContext.id : user.id,
    name,
    facility_name: name,
    city,
    state,
    section_id: "",
    section_name: user.sectionName ?? "",
    legacy_id: user.expandContext?.type === "Facility" ? (user.expandContext.legacyId ?? undefined) : undefined
  };
}

export function mentionListItemProps(user: MentionUser, query = ""): ListItemProps {
  const headline = mentionLabel(user);

  if (!user.kind || user.kind === "teammate") {
    return {
      compact: true,
      useAvatar: true,
      picture: user.photo,
      iconName: "person",
      headline: entityHeadline(headline, headline, query),
      supportingText: user.email || user.subtitle,
      centerLeadingIcon: true
    };
  }

  if (user.kind === "person") {
    return globalSearchPersonListItemProps(mentionPersonHit(user), query);
  }

  if (user.kind === "facility") {
    return globalSearchFacilityListItemProps(mentionFacilityHit(user), query);
  }

  return {
    compact: true,
    iconName: user.iconName || "link",
    iconBackground: true,
    headline: entityHeadline(headline, headline, query),
    supportingText: user.subtitle,
    centerLeadingIcon: true,
    ...(user.kind === "event" && user.expandContext?.type === "Event" ? eventStatusTag(user.expandContext.status) : {})
  };
}
